import api from '@/services/Api';
import Person from '@/types/Person';
import { successSwal, warningSwal } from '@/helper/swals';
import { PersonenRolle } from '@/types/PersonenRolle';
import { ActionContext, Module } from 'vuex';
import { AxiosResponse } from 'axios';
import { PersonenState, RootState } from '@/store/types';

export type PersonenFilter = { impfstelle_id?: number; gueltig?: string };
export type LoadPersonenPayload = { filter: PersonenFilter };
export type UpdatePersonPayload = { person: Person; filter: PersonenFilter };
export type DeletePersonPayload = { person: Person; filter: PersonenFilter };

type Context = ActionContext<PersonenState, RootState>;

export const personen: Module<PersonenState, RootState> = {
    namespaced: true,
    state: {
        dialogs: {
            edit: false,
        },
        loaders: {
            save: false,
            delete: false,
            personen: false,
        },
        personen: [],
    },
    actions: {
        loadPersonen({ commit, rootState }: Context, payload?: LoadPersonenPayload): void {
            commit('setLoadersPersonen', true);

            const data = {
                impfstelle_id: rootState.authentication.impfstelle_id,
                ...payload,
            };

            api.post('verwaltung/personen/load', data)
                .then((response) => commit('setPersonen', response.data.body))
                .catch(console.error)
                .finally(() => commit('setLoadersPersonen', false));
        },
        updatePerson({ commit, dispatch }: Context, { person, filter }: UpdatePersonPayload): void {
            commit('setLoadersSave', true);

            api.post('verwaltung/personen/speichern', person)
                .then(() => {
                    successSwal('Person', 'gespeichert');
                    dispatch('loadPersonen', { filter });
                    commit('setDialogsDetail', false);
                })
                .catch(console.error)
                .finally(() => commit('setLoadersSave', false));
        },
        deletePerson({ commit, dispatch }: Context, { person, filter }: DeletePersonPayload): void {
            commit('setLoadersDelete', true);

            api.post('verwaltung/personen/delete', person)
                .then(({ data }: AxiosResponse) => {
                    if (data?.body) {
                        successSwal('Person', 'gelöscht');
                        dispatch('loadPersonen', { filter });
                        commit('setDialogsDetail', false);
                    } else {
                        warningSwal(
                            'Löschen',
                            'Diese Person darf nicht gelöscht werden, da sie bereits in Impfungen eingetragen wurde.',
                        );
                    }
                })
                .catch(console.error)
                .finally(() => commit('setLoadersDelete', false));
        },
    },
    mutations: {
        setPersonen(state: PersonenState, payload: Partial<Person>[]): void {
            state.personen = payload.map((data) => new Person(data));
        },
        setDialogsDetail(state: PersonenState, visible: boolean): void {
            state.dialogs.edit = visible;
        },
        setLoadersSave(state: PersonenState, loading: boolean): void {
            state.loaders.save = loading;
        },
        setLoadersDelete(state: PersonenState, loading: boolean): void {
            state.loaders.delete = loading;
        },
        setLoadersPersonen(state: PersonenState, loading: boolean): void {
            state.loaders.personen = loading;
        },
    },
    getters: {
        getPersonen: (state: PersonenState): Person[] => {
            return state.personen.filter(({ gueltig, rolle }: Person) => {
                return gueltig === 'j' && rolle === PersonenRolle.Arzt;
            });
        },
        getAssistenten: (state: PersonenState): Person[] => {
            return state.personen.filter(({ gueltig, rolle }: Person) => {
                return gueltig === 'j' && rolle === PersonenRolle.Assistenz;
            });
        },
    },
};
